import React, { useEffect } from 'react';
import { Box } from '@opendoor/bricks/core';

import { FormRadioInput, NO, YES } from 'sellDirect/components/closingDash/form/FormRadioInput';
import { FormTextInput } from 'sellDirect/components/closingDash/form/FormTextInput';
import {
  COOLING_SYSTEM_OPTIONS,
  HEATING_SYSTEM_OPTIONS,
} from 'sellDirect/helpers/closingDash/disclosures';
import { IResetableFormRenderProps } from 'sellDirect/components/closingDash/ResetableForm';

import { text } from 'sellDirect/components/closingDash/content';
import { DisclosuresFormState } from 'sellDirect/components/closingDash/disclosures/DisclosuresForm';

interface DisclosuresUtilitiesFormProps {
  loading?: boolean;
  shouldSeeAgentExperience: boolean;
  resetableForm: IResetableFormRenderProps;
}

/**
 * Editable disclosures and utilities form.
 *
 * component-test-check-ignore
 * storybook-check-ignore
 */
export const DisclosuresUtilitiesFormContent: React.FC<DisclosuresUtilitiesFormProps> = ({
  resetableForm,
  shouldSeeAgentExperience,
  loading,
}) => {
  const { onUpdate } = resetableForm;
  const formState: DisclosuresFormState = resetableForm?.formState;
  const { hasExistingWaterIssue, hasSewerSepticIssue } = formState;

  useEffect(() => {
    if (hasExistingWaterIssue === NO) {
      onUpdate('hasExistingWaterIssueDetails', undefined);
    }
    // eslint-disable-next-line
  }, [hasExistingWaterIssue]);

  useEffect(() => {
    if (hasSewerSepticIssue === NO) {
      onUpdate('hasSewerSepticIssueDetails', undefined);
    }
    // eslint-disable-next-line
  }, [hasSewerSepticIssue, onUpdate]);

  return (
    <>
      <FormRadioInput
        disabled={loading}
        inputName="heatingType"
        title={text.disclosuresPage.serviceAccounts.serviceAccountHeatingType(
          shouldSeeAgentExperience,
        )}
        onUpdate={onUpdate}
        options={HEATING_SYSTEM_OPTIONS}
        content={formState?.heatingType}
        mb={3}
      />
      <FormRadioInput
        disabled={loading}
        inputName="coolingType"
        title={text.disclosuresPage.serviceAccounts.serviceAccountCoolingType(
          shouldSeeAgentExperience,
        )}
        onUpdate={onUpdate}
        options={COOLING_SYSTEM_OPTIONS}
        content={formState?.coolingType}
        mb={3}
      />
      <FormRadioInput
        disabled={loading}
        inputName="hasExistingWaterIssue"
        title={text.disclosuresPage.serviceAccounts.hasWaterIssue(shouldSeeAgentExperience)}
        onUpdate={onUpdate}
        content={formState?.hasExistingWaterIssue}
        mb={3}
      />
      {formState?.hasExistingWaterIssue === YES && (
        <Box ml={8}>
          <FormTextInput
            disabled={loading}
            inputName="hasExistingWaterIssueDetails"
            title={text.disclosuresPage.serviceAccounts.waterIssueDetails}
            onUpdate={onUpdate}
            content={formState?.hasExistingWaterIssueDetails}
          />
        </Box>
      )}
      <FormRadioInput
        disabled={loading}
        inputName="hasSewerSepticIssue"
        title={text.disclosuresPage.serviceAccounts.hasSewerSepticIssue(shouldSeeAgentExperience)}
        onUpdate={onUpdate}
        content={formState?.hasSewerSepticIssue}
        mb={3}
      />
      {formState?.hasSewerSepticIssue === YES && (
        <Box ml={8}>
          <FormTextInput
            disabled={loading}
            inputName="hasSewerSepticIssueDetails"
            title={text.disclosuresPage.serviceAccounts.septicIssueDetails}
            onUpdate={onUpdate}
            content={formState?.hasSewerSepticIssueDetails}
          />
        </Box>
      )}
    </>
  );
};
